import { formatPercent } from "../../lib/format";

interface UtilizationProps {
  /** Percentage measured by telemetry, or null when it is unavailable. */
  value: number | null;
  /** How many GPUs the figure covers. */
  gpus: number;
}

/**
 * How hard the GPUs are working, or an honest blank.
 *
 * Args:
 *     value: Measured utilization, or null.
 *     gpus: Number of GPUs held.
 *
 * Returns:
 *     A labelled meter, or a sentence saying the number is not measured.
 */
export function Utilization({ value, gpus }: UtilizationProps): JSX.Element {
  if (value === null) {
    return (
      <p className="util__unknown">
        How busy your {gpus === 1 ? "GPU is" : "GPUs are"} cannot be measured
        right now.
      </p>
    );
  }

  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div className="util">
      <div
        className="util__track"
        role="meter"
        aria-label={`GPU activity across your ${gpus === 1 ? "GPU" : "GPUs"}`}
        aria-valuenow={Math.round(clamped)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={`${Math.round(clamped)} percent`}
      >
        <div className="util__fill" style={{ width: `${clamped}%` }} />
      </div>
      <span className="util__value mono">{formatPercent(value)}</span>
    </div>
  );
}
